import { api, descargarArchivo } from "@/lib/api"

/**
 * EL GRUPO. Modulo `consolidation` del backend.
 *
 *   GET  /api/consolidacion/{periodo}            ?moneda&libro
 *   POST /api/consolidacion/{periodo}/registrar  ?moneda&libro   (body: motivo)
 *   GET  /api/consolidacion/{periodo}/corridas
 *   GET  /api/consolidacion/estructura/actual    ?a_fecha
 *   PUT  /api/consolidacion/estructura
 *   GET  /api/consolidacion/intercompania/contrapartes
 *   GET  /api/consolidacion/intercompania/sugerencias
 *   POST /api/consolidacion/intercompania/confirmar
 *   GET  /api/consolidacion/tesoreria/caja       ?moneda&a_fecha
 *
 * NO lleva `entity_id`, y esa es la diferencia de fondo con el resto de la SPA:
 * el consolidado cruza TODAS las sociedades por definicion, asi que pedirlo por
 * una sola no significa nada.
 *
 * PERO EL ALCANCE DE QUIEN MIRA SI LO ACOTA, y eso llega en la respuesta y no
 * en la peticion. Quien esta acotado a una sociedad recibe el total del grupo
 * —correcto y entero— con el detalle de las demas sumado bajo la clave
 * `"otras"`. Cada respuesta trae su bloque `alcance` para poder DECIRLO: una
 * cifra recortada que no se anuncia se lee como si fuera completa. La regla y
 * su porque viven en `backend/app/modules/consolidation/alcance.py`.
 *
 * Como en `statements/api.ts`, no se traduce a camelCase: la forma que devuelve
 * el backend ES la del consolidado, y renombrarla obligaria a mantener dos
 * vocabularios para cada subtotal.
 */

/**
 * Hasta donde llega quien esta mirando.
 *
 * `completo: false` significa que los TOTALES son del grupo entero y correctos,
 * y que el desglose por sociedad esta parcialmente agregado. No es un error ni
 * un dato faltante: es un limite de acceso, y por eso viaja dicho.
 */
export interface AlcanceDeGrupo {
  completo: boolean
  entidades_visibles: string[]
  sociedades_agregadas: number
  /** La clave con la que viaja lo agregado. Siempre `"otras"`. */
  clave_agregado?: string
  nota: string | null
}

/** La clave de la fila que suma lo que no esta al alcance. No es un UUID. */
export const CLAVE_OTRAS = "otras"

/** Lo que el consolidado NO pudo resolver. Se pinta SIEMPRE, antes del numero. */
export interface Advertencia {
  clave: string
  grave: boolean
  texto: string
}

export interface SociedadDelPerimetro {
  entity_id: string
  razon_social: string
  tax_id: string
  pais: string
  moneda_funcional: string
  porcentaje: string | number
  metodo: "integracion_global" | "participacion" | "costo"
  es_cabecera: boolean
  matriz_entity_id: string | null
  /** `false` = el porcentaje es un SUPUESTO del sistema, no un dato declarado. */
  declarado: boolean
  /** `false` = entra en el total del grupo, pero su detalle no esta a tu alcance. */
  detalle_visible?: boolean
}

export interface Perimetro {
  sociedades: SociedadDelPerimetro[]
  supuesto: boolean
  sin_declarar: string[]
  nota: string | null
}

export interface ConversionDeSociedad {
  entity_id: string
  razon_social: string
  pais?: string
  moneda_funcional: string
  cierre?: string | number | null
  promedio?: string | number | null
  historico?: string | number | null
  ajuste_por_conversion?: string | number
  cuentas?: number
  sin_libro?: boolean
  nota?: string
  /** Fila sintetica: suma las sociedades cuyo detalle no esta a tu alcance. */
  agregado?: boolean
  sociedades?: number
  sin_libro_en?: number
  detalle_tasa?: { dias?: number | null; cierre_de?: string | null; nota?: string; historico_de?: string | null; nota_historico?: string }
}

export interface Eliminacion {
  tipo: string
  entity_a_id: string
  entity_b_id: string | null
  account_group_codigo: string
  nombre?: string | null
  importe: number
  motivo: string
  /** Suma de eliminaciones registradas por sociedades fuera de tu alcance. */
  agregado?: boolean
  patas?: number
}

export interface Asimetria {
  entity_a_id: string
  entity_b_id: string
  saldo_a: number
  saldo_b: number
  diferencia: number
  cuadra: boolean | null
  comparable: boolean
  monedas: string[]
  explicacion: string
}

export interface SeccionDeEstado {
  codigo: string
  clave: string
  etiqueta: string
  importe: number
  cuentas: Array<{ codigo: string; nombre: string; importe: number; derivado?: boolean }>
}

export interface Consolidado {
  periodo: string
  desde: string
  hasta: string
  moneda_presentacion: string
  perimetro: Perimetro
  conversion: ConversionDeSociedad[]
  tipos_de_cambio: {
    cierre: Record<string, string>
    promedio: Record<string, string>
    historico?: Record<string, string>
    detalle: Record<string, { dias?: number | null; cierre_de?: string | null; nota?: string }>
  }
  ajuste_por_conversion: number
  eliminaciones: Eliminacion[]
  intercompania: {
    movimientos: number
    asimetrias: Asimetria[]
    sin_cuadrar: Asimetria[]
    /** Asimetrias entre dos sociedades que no estan a tu alcance. */
    asimetrias_fuera_de_alcance?: number
  }
  participacion_no_controladora: number
  participacion_no_controladora_detalle: Array<{
    entity_id: string
    porcentaje_del_grupo: string | number
    porcentaje_minoritario: string | number
    patrimonio: number
    resultado_del_periodo: number
    participacion_no_controladora: number
  }>
  balance: {
    secciones?: SeccionDeEstado[]
    cuadre?: { cuadra: boolean; diferencia: number }
    totales?: Record<string, number>
    libro_vacio?: boolean
  } | null
  resultado: {
    secciones?: SeccionDeEstado[]
    subtotales?: Record<string, number>
    libro_vacio?: boolean
  } | null
  descuadre: number
  base_normativa: { libro: string; es_niif: boolean; nota?: string }
  advertencias: Advertencia[]
  alcance?: AlcanceDeGrupo
}

export interface CuentaDeCaja {
  bank_account_id: string
  nombre: string
  moneda: string | null
  saldo: number
  saldo_presentacion?: number
  origen: string
  entity_id: string | null
  razon_social: string | null
  nota?: string
  sugerencia?: { entity_id: string; razon_social: string; motivo: string } | null
}

export interface CajaDelGrupo {
  a_fecha: string
  moneda_presentacion: string
  por_sociedad: Array<{
    entity_id: string
    razon_social: string
    pais: string | null
    moneda_funcional: string | null
    total: number
    cuentas: CuentaDeCaja[]
    /** Fila sintetica: la caja de las sociedades fuera de tu alcance, sumada. */
    agregado?: boolean
    sociedades?: number
    nota?: string
  }>
  por_moneda: Array<{
    moneda: string
    saldo: number
    saldo_presentacion: number
    cuentas: number
    tipo_cambio: string
  }>
  sin_asignar: CuentaDeCaja[]
  sin_moneda: CuentaDeCaja[]
  total: number
  total_asignado: number
  total_sin_asignar: number
  tipos_de_cambio: Record<string, string>
  advertencias: Advertencia[]
  alcance?: AlcanceDeGrupo
  /**
   * Con alcance parcial la tesoreria va MAS CERRADA que el consolidado: el
   * desglose por moneda desaparece —con cuatro sociedades y cuatro monedas
   * funcionales, «el total en PAB» es la caja de Panama con otro nombre— y las
   * cuentas sin sociedad se cuentan sin listarse.
   */
  por_moneda_oculto?: boolean
  sin_asignar_cuentas?: number
  sin_moneda_cuentas?: number
}

export interface Sugerencia {
  party_id: string
  party_razon_social: string
  party_tax_id: string
  party_pais: string
  es_cliente: boolean
  es_proveedor: boolean
  /** Nulo cuando el nombre empata con varias sociedades: ver `empata_con`. */
  entity_id: string | null
  entity_razon_social: string | null
  entity_tax_id: string | null
  parecido: number
  por_que_no_se_detecto: string
  /** `true` = se parece igual a varias, y Marty NO elige por ti. */
  ambigua?: boolean
  empata_con?: Array<{ entity_id: string; razon_social: string; pais: string }>
  por_que_no_se_propone?: string | null
}

export interface Contraparte {
  party_id: string
  entity_id: string
  razon_social: string
  party_razon_social: string
  origen: string
}

export function fetchConsolidado(
  periodo: string,
  moneda: string,
  libro: string,
): Promise<Consolidado> {
  return api.get<Consolidado>(`/consolidacion/${periodo}?moneda=${moneda}&libro=${libro}`)
}

export function fetchPerimetro(aFecha?: string): Promise<Perimetro> {
  return api.get<Perimetro>(
    `/consolidacion/estructura/actual${aFecha ? `?a_fecha=${aFecha}` : ""}`,
  )
}

export function fetchCaja(moneda: string, aFecha?: string): Promise<CajaDelGrupo> {
  return api.get<CajaDelGrupo>(
    `/consolidacion/tesoreria/caja?moneda=${moneda}${aFecha ? `&a_fecha=${aFecha}` : ""}`,
  )
}

export function fetchContrapartes(): Promise<{
  contrapartes: Contraparte[]
  total: number
  nota: string | null
}> {
  return api.get("/consolidacion/intercompania/contrapartes")
}

export function fetchSugerencias(): Promise<Sugerencia[]> {
  return api.get<Sugerencia[]>("/consolidacion/intercompania/sugerencias")
}

export function confirmarPar(entrada: {
  party_id: string
  entity_id: string
  motivo: string
}): Promise<{ id: string }> {
  return api.post("/consolidacion/intercompania/confirmar", entrada)
}

export function declararParticipacion(entrada: {
  entity_id: string
  matriz_entity_id: string | null
  porcentaje: number
  metodo: string
  vigente_desde: string
  nota?: string | null
  motivo: string
}): Promise<{ id: string; sustituye_a: string | null }> {
  return api.put("/consolidacion/estructura", entrada)
}

/* ==========================================================================
   EL MAPA DE OBLIGACIONES
   Qué debe cada sociedad, ante quién, cuándo — y hasta dónde llega Marty con
   cada cosa. Modulo `obligations` del backend.
   ========================================================================== */

/** Hasta dónde llega el sistema. `la_presento` es hoy cero en los cuatro países. */
export type NivelObligacion = "la_conozco" | "la_preparo" | "la_presento"

export interface ObligacionDelMapa {
  codigo: string
  nombre: string
  tipo: string
  vence: string | null
  sale_de_caja: string | null
  direccion: string
  formulario: string | null
  autoridad: string
  obligatoria: boolean
  depende_de: string[]
  fundamento_legal: string | null
  nota: string | null
  nivel: NivelObligacion
}

export interface MapaDePais {
  pais: string
  /**
   * Presente SOLO cuando el rulepack de ese país falló. La respuesta conserva
   * la misma forma —con `obligaciones: []` y su advertencia grave— para que un
   * país roto no tumbe el mapa de los otros tres, que es lo que pasaba.
   */
  error?: string
  entity_id?: string
  razon_social?: string
  version_rulepack: string
  madurez: string
  moneda: string
  autoridad: string
  periodo: string
  regimen_evaluado: string
  regimen_declarado: boolean
  empleados_evaluados: number
  obligaciones: ObligacionDelMapa[]
  resumen: { la_conozco: number; la_preparo: number; la_presento: number }
  pendientes_de_verificar: number
  pendientes: Array<{ regla: string; falta: string; fundamento_legal: string | null }>
  advertencias: Advertencia[]
}

export interface MapaDelGrupo {
  periodo: string
  sociedades: MapaDePais[]
  total_obligaciones: number
  se_preparan: number
  se_presentan: number
  sin_rulepack: string[]
  nota: string
}

export function fetchMapaDelGrupo(periodo: string): Promise<MapaDelGrupo> {
  return api.get<MapaDelGrupo>(`/obligaciones/grupo?periodo=${periodo}`)
}

/* ==========================================================================
   EL ANALISIS DEL GRUPO
   Lo que el consolidado NO contesta: si va mejor o peor, que sociedad tira y
   cual frena, de cuantos clientes depende el ingreso y cuantos meses de caja
   hay. Modulo `consolidation/analisis` del backend.

     GET /api/consolidacion/{periodo}/analisis         ?meses&moneda&libro
     GET /api/consolidacion/{periodo}/analisis/export  ?meses&moneda&libro
   ========================================================================== */

/** Un mes de la serie. `sin_datos` = no se pudo consolidar, y NO vale cero. */
export interface MesDelGrupo {
  periodo: string
  sin_datos?: boolean
  motivo?: string
  ingresos?: number
  costo_de_ventas?: number
  utilidad_bruta?: number
  ebitda?: number
  utilidad_neta?: number
  margen_neto?: number | null
  eliminado_del_resultado?: number
  sociedades?: number
}

/**
 * Una variacion contra otro mes.
 *
 * `porcentaje` es `null` cuando NO significa lo que parece: base cero —no hay
 * de que sacar porcentaje— o base NEGATIVA. Lo segundo es el caso sutil: de
 * −100 a −50 da «+50%» y se lee como que mejoro la mitad; de −100 a −200 da
 * «−100%» y se lee como una caida, cuando la perdida se duplico. Con base
 * negativa el unico numero que quiere decir lo que parece es `absoluto`.
 */
export interface Cambio {
  porcentaje: number | null
  absoluto: number
  base_negativa: boolean
  base: number
}

export interface Variacion {
  periodo: string | null
  ingresos: Cambio | null
  utilidad_neta: Cambio | null
  nota?: string | null
}

export interface Tendencia {
  periodo: string
  ingresos: number
  utilidad_neta: number
  margen_neto: number | null
  vs_mes_anterior: Variacion
  vs_mismo_mes_del_anio_pasado: Variacion
}

/** El aporte de una sociedad, ANTES de eliminar lo intercompañia. */
export interface SociedadComparada {
  entity_id: string
  razon_social: string | null
  pais: string | null
  moneda_funcional: string | null
  ingresos: number
  costo_de_ventas: number
  utilidad_bruta: number
  ebitda: number
  utilidad_neta: number
  margen_bruto: number | null
  margen_neto: number | null
  peso_en_el_ingreso: number | null
  aporte_al_resultado: number | null
  /** Fila sintetica: las sociedades fuera de tu alcance, sumadas. */
  agregado?: boolean
  sociedades?: number
}

export interface LineaDeConcentracion {
  entity_id?: string
  party_id?: string
  razon_social: string | null
  pais: string | null
  importe: number
  cuota: number | null
  acumulado?: number
  agregado?: boolean
  sociedades?: number
}

export interface Concentracion {
  total: number
  lineas: LineaDeConcentracion[]
  /** Herfindahl: 10.000 es un solo cliente; tiende a cero con muchos parejos. */
  hhi: number
  top_1: number | null
  top_3: number | null
  cuantos: number
  /** `null` = no hubo ingreso, asi que NO hay nivel. No es «baja». */
  nivel: "alta" | "moderada" | "baja" | null
  sin_ingreso?: boolean
  fuente?: string
  periodo?: string
  monedas_sin_cotizacion?: string[]
}

export interface RunwayDeSociedad {
  entity_id: string
  razon_social: string | null
  pais: string | null
  caja: number
  burn_mensual: number
  /** `null` = no hay quema. No es «infinito»: es que no aplica. */
  meses: number | null
  sin_caja_asignada?: boolean
}

export interface Runway {
  consolidado: {
    caja: number
    burn_mensual: number
    meses: number | null
    caja_sin_asignar: number
  }
  por_sociedad: RunwayDeSociedad[]
  el_mas_corto: RunwayDeSociedad | null
  el_mas_corto_oculto?: boolean
  sociedades_fuera_de_alcance?: number
  meses_de_burn: number
  nota: string
  base: string
}

export interface AnalisisDelGrupo {
  periodo: string
  periodos: string[]
  meses: number
  moneda_presentacion: string
  libro: string
  evolucion: MesDelGrupo[]
  tendencia: Tendencia | null
  comparativa: SociedadComparada[]
  /**
   * EL PUENTE. La suma de las sociedades NO da el consolidado: se pasa por lo
   * que el grupo se factura a si mismo. Sin esto, quien sume la columna y no le
   * de el consolidado busca un error que no existe.
   */
  suma_de_aportes: {
    ingresos: number
    utilidad_neta: number
    eliminado_del_resultado: number
    utilidad_neta_consolidada: number
  } | null
  concentracion: {
    por_sociedad: Concentracion
    por_cliente: Concentracion | null
    /** Con alcance parcial, por que la cartera de clientes no se muestra. */
    por_cliente_oculto?: string
  } | null
  runway: Runway | null
  perimetro: Perimetro | null
  advertencias: Advertencia[]
  alcance?: AlcanceDeGrupo
}

export function fetchAnalisis(
  periodo: string,
  meses: number,
  moneda: string,
  libro: string,
): Promise<AnalisisDelGrupo> {
  return api.get<AnalisisDelGrupo>(
    `/consolidacion/${periodo}/analisis?meses=${meses}&moneda=${moneda}&libro=${libro}`,
  )
}

/**
 * El CSV, con la misma consulta que la pantalla.
 *
 * Se descarga con `descargarArchivo` y no con un enlace: la peticion necesita
 * el token y el tenant activo, y un `<a href>` no los lleva — el backend
 * respondería 401 y el navegador guardaría el error con extension .csv.
 */
export function exportarAnalisis(
  periodo: string,
  meses: number,
  moneda: string,
  libro: string,
): Promise<void> {
  return descargarArchivo(
    `/consolidacion/${periodo}/analisis/export?meses=${meses}&moneda=${moneda}&libro=${libro}`,
    `analisis-grupo-${periodo}.csv`,
  )
}

/**
 * EL PAGO DE IMPUESTOS DEL GRUPO: lo pagado y lo que viene.
 *
 *   GET /api/finance/impuestos ?anio&pais
 *
 * Vive en `finance` y no en `consolidacion` porque sale del modelo financiero
 * —el bloque `cashflow` de `financial_fact`— y no del motor de consolidación. Lo
 * consume Holding, que es donde se pregunta por el grupo.
 */
export interface PagoDeImpuestos {
  anio: number
  pais: string | null
  moneda: string
  corte: { mes: number | null; regla: string | null; regla_id: string | null }
  meses: {
    real: Record<string, number>
    proyectado: Record<string, number>
  }
  trimestres: {
    trimestre: string
    real: number
    proyectado: number
    /** El corte lo parte por la mitad: su total no es ni pagado ni estimado. */
    mixto: boolean
  }[]
  anual: { real: number; proyectado: number; total: number }
  por_pais: {
    pais: string
    total_real: number
    total_proyectado: number
  }[]
  nota: string
}

export function fetchPagoDeImpuestos(anio: number, pais?: string): Promise<PagoDeImpuestos> {
  return api.get<PagoDeImpuestos>("/finance/impuestos", { query: { anio, pais } })
}
